import React, { useState } from "react";
import { Link } from "react-router-dom";
import dateFormat from "../../lib/dateFormat";

const SportsSection = ({ posts, loading }) => {
  const [isImageLoading, setIsImageLoading] = useState({});
  const [hoveredPost, setHoveredPost] = useState(null);

  // Handle image loading states
  const handleImageLoad = (postId) => {
    setIsImageLoading((prev) => ({ ...prev, [postId]: false }));
  };

  const handleImageError = (postId) => {
    setIsImageLoading((prev) => ({ ...prev, [postId]: false }));
  };

  // Calculate reading time for articles
  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime;
  };

  // Enhanced loading skeleton for featured post
  const FeaturedSkeleton = () => (
    <div className="sport-featured-skeleton">
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-category"></div>
        <div className="skeleton-title"></div>
        <div className="skeleton-excerpt"></div>
        <div className="skeleton-meta"></div>
      </div>
    </div>
  );

  // Enhanced loading skeleton for sport cards
  const SportCardSkeleton = () => (
    <div className="sport-card-skeleton">
      <div className="skeleton-card-image"></div>
      <div className="skeleton-card-content">
        <div className="skeleton-card-category"></div>
        <div className="skeleton-card-title"></div>
        <div className="skeleton-card-date"></div>
      </div>
    </div>
  );

  return (
    <section className="sports-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">sport</h2>
          <Link to="/category/sports" className="see-more-btn">
            shiko më shumë <i className="fas fa-arrow-right"></i>
          </Link>
        </div>

        <div className="row">
          {/* Main Featured Sport */}
          <div className="col-lg-6">
            {loading ? (
              <FeaturedSkeleton />
            ) : posts && posts.length > 0 && posts[0] ? (
              <article
                className={`sport-featured ${hoveredPost === posts[0].id ? "hovered" : ""}`}
                onMouseEnter={() => setHoveredPost(posts[0].id)}
                onMouseLeave={() => setHoveredPost(null)}
              >
                <Link
                  to={`/news/${posts[0].id}`}
                  className="sport-featured-link"
                >
                  <div className="sport-featured-image">
                    {isImageLoading[posts[0].id] !== false && (
                      <div className="image-skeleton">
                        <div className="skeleton-loader"></div>
                      </div>
                    )}
                    <img
                      src={posts[0].image_url}
                      alt={posts[0].title}
                      className={
                        isImageLoading[posts[0].id] === false
                          ? "loaded"
                          : "loading"
                      }
                      onLoad={() => handleImageLoad(posts[0].id)}
                      onError={() => handleImageError(posts[0].id)}
                      loading="lazy"
                    />
                    <div className="sport-featured-overlay">
                      <span className="sport-category">
                        <i className="fas fa-futbol"></i>
                        {posts[0].categories?.[0] || "Sport"}
                      </span>
                      <div className="sport-featured-badge">
                        <i className="fas fa-star"></i>
                        Kryesore
                      </div>
                    </div>
                  </div>
                  <div className="sport-featured-content">
                    <div className="sport-featured-meta">
                      <span className="sport-date">
                        <i className="far fa-clock"></i>
                        {dateFormat(posts[0].created_at)}
                      </span>
                      <span className="sport-reading-time">
                        <i className="fas fa-book-open"></i>
                        {calculateReadingTime(posts[0].content)} min lexim
                      </span>
                    </div>
                    <h2 className="sport-featured-title">{posts[0].title}</h2>
                    <p
                      className="sport-featured-excerpt"
                      dangerouslySetInnerHTML={{
                        __html:
                          posts[0].content.slice(0, 200) +
                          (posts[0].content.length > 200 ? "..." : ""),
                      }}
                    />
                    <div className="sport-featured-footer">
                      <div className="sport-featured-cta">
                        <span>Lexo më shumë</span>
                        <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </article>
            ) : (
              <div className="no-posts-message">
                <div className="no-posts-icon">
                  <i className="fas fa-futbol"></i>
                </div>
                <h3>Nuk ka lajme sportive</h3>
                <p>Nuk ka lajme sportive të disponueshme për momentin.</p>
                <Link to="/category/sports" className="browse-sports-btn">
                  <i className="fas fa-search"></i>
                  Shfleto kategorinë
                </Link>
              </div>
            )}
          </div>

          {/* Side Sports News */}
          <div className="col-lg-6">
            <div className="sport-grid">
              {loading ? (
                // Show 4 skeleton cards while loading
                Array.from({ length: 4 }).map((_, index) => (
                  <SportCardSkeleton key={`skeleton-${index}`} />
                ))
              ) : posts?.slice(1).length > 0 ? (
                posts.slice(1).map((post) => (
                  <article
                    key={post.id}
                    className={`sport-card ${hoveredPost === post.id ? "hovered" : ""}`}
                    onMouseEnter={() => setHoveredPost(post.id)}
                    onMouseLeave={() => setHoveredPost(null)}
                  >
                    <Link to={`/news/${post.id}`} className="sport-card-link">
                      <div className="sport-card-wrapper">
                        <div className="sport-card-image">
                          {isImageLoading[post.id] !== false && (
                            <div className="image-skeleton">
                              <div className="skeleton-loader"></div>
                            </div>
                          )}
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className={
                              isImageLoading[post.id] === false
                                ? "loaded"
                                : "loading"
                            }
                            onLoad={() => handleImageLoad(post.id)}
                            onError={() => handleImageError(post.id)}
                            loading="lazy"
                          />
                          <div className="sport-card-overlay">
                            <span className="sport-card-category">
                              <i className="fas fa-futbol"></i>
                              {post.categories?.[0] || "Sport"}
                            </span>
                          </div>
                        </div>
                        <div className="sport-card-content">
                          <div className="sport-card-meta">
                            <span className="sport-card-date">
                              <i className="far fa-clock"></i>
                              {dateFormat(post.created_at)}
                            </span>
                            <span className="sport-card-reading-time">
                              <i className="fas fa-book-open"></i>
                              {calculateReadingTime(post.content)} min
                            </span>
                          </div>
                          <h3 className="sport-card-title">{post.title}</h3>
                          <div className="sport-card-footer">
                            <div className="sport-card-arrow">
                              <i className="fas fa-arrow-right"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))
              ) : (
                <div className="no-side-posts-message">
                  <div className="no-posts-icon">
                    <i className="fas fa-newspaper"></i>
                  </div>
                  <p>Nuk ka lajme të tjera sportive</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SportsSection;
